import { useMemo, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { de, enUS } from "date-fns/locale";
import type { Matcher } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import {
  businessDateFromCalendar,
  calendarDateFromBusiness,
  dateFieldYearWindow,
} from "@/lib/calendar-date";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { berlinToday, isBusinessDate } from "@/shared/dates";

export interface DateFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  testId?: string;
  "data-testid"?: string;
  "aria-label"?: string;
  className?: string;
}

/**
 * Shared business-date control. Value is always `YYYY-MM-DD` (or `""`).
 *
 * Playwright: `#id` / `data-testid` is an opacity-0 `type="text"` overlay so
 * `page.locator("#assignment-entry").fill(iso)` still works and is not clamped by
 * native `type="date"` min/max. Prefer `fillDateField` in `e2e/helpers.ts`.
 * The visible picker (not the overlay) enforces min/max by disabling days.
 */
export function DateField({
  id,
  value,
  onChange,
  min,
  max,
  disabled,
  required,
  testId,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
  className,
}: DateFieldProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const fieldTestId = testId ?? dataTestId;

  const selected = value && isBusinessDate(value) ? calendarDateFromBusiness(value) : undefined;
  const minDate = min && isBusinessDate(min) ? calendarDateFromBusiness(min) : undefined;
  const maxDate = max && isBusinessDate(max) ? calendarDateFromBusiness(max) : undefined;
  const todayDate = calendarDateFromBusiness(berlinToday());
  const { fromYear, toYear } = dateFieldYearWindow({ min, max, value });
  const defaultMonth = selected ?? minDate ?? todayDate;
  const locale = i18n.language.startsWith("en") ? enUS : de;
  const display = value && isBusinessDate(value) ? formatDate(value) : t("dateField.placeholder");

  const disabledMatchers = useMemo(() => {
    const matchers: Matcher[] = [];
    if (minDate) matchers.push({ before: minDate });
    if (maxDate) matchers.push({ after: maxDate });
    return matchers.length > 0 ? matchers : undefined;
  }, [minDate, maxDate]);

  function commitIso(next: string) {
    if (next === "" || isBusinessDate(next)) onChange(next);
  }

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <div
            aria-hidden
            data-testid={fieldTestId ? `${fieldTestId}-trigger` : undefined}
            className={cn(
              "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm",
              !(value && isBusinessDate(value)) && "text-muted-foreground",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <span className="truncate">{display}</span>
            <CalendarIcon className="size-4 shrink-0 opacity-50" />
          </div>
          <input
            id={id}
            type="text"
            inputMode="none"
            autoComplete="off"
            spellCheck={false}
            value={value}
            min={min}
            max={max}
            required={required}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-haspopup="dialog"
            aria-expanded={open}
            data-testid={fieldTestId}
            tabIndex={disabled ? -1 : 0}
            className={cn(
              "absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed",
              "[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0",
            )}
            onChange={(event) => commitIso(event.target.value)}
            onPointerDown={(event) => {
              if (disabled) return;
              event.preventDefault();
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (disabled) return;
              if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
                event.preventDefault();
                setOpen(true);
              }
            }}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-2"
        align="start"
        data-testid="date-field-calendar"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <Calendar
          mode="single"
          locale={locale}
          aria-label={t("dateField.openCalendar")}
          selected={selected}
          defaultMonth={defaultMonth}
          today={todayDate}
          startMonth={new Date(fromYear, 0, 1)}
          endMonth={new Date(toYear, 11, 1)}
          disabled={disabledMatchers}
          labels={{
            labelMonthDropdown: () => t("dateField.month"),
            labelYearDropdown: () => t("dateField.year"),
          }}
          onSelect={(date) => {
            if (!date) {
              if (!required) onChange("");
              return;
            }
            onChange(businessDateFromCalendar(date));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
